"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Microscope, Layers, Activity, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, SlidersHorizontal as Sliders, Sparkles, FileCheck2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
const technologyCategories = [{
  id: "spectroscopy",
  label: "NIR & Optical Testing",
  icon: Sparkles,
  badge: "Process & Lab QA",
  title: "Near-Infrared (NIR) & Raman Spectroscopy",
  description: "Rapid, non-destructive quantitative and qualitative analysis for moisture, protein, fat, ash, and blend consistency across raw ingredients and finished food formulations.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/nir_spectroscopy_instrument.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/nir_spectroscopy_instrument.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/nir_spectroscopy_instrument.webp-wvc-sizes",
    alt: "NIR and optical analytical testing instrument in food lab"
  },
  pageLink: "/ProcessAnalyticalTechnologyPage",
  keyParameters: ["Moisture, fat, protein, sugar, and dry matter measurement, application dependent", "Non-contact measurement for liquids, slurries, powders, and grains", "Direct integration into batch blenders, dryers, and conveyor systems", "Secure data recording with traceable audit trails"],
  specs: [{
    label: "Analysis Time",
    value: "Application dependent"
  }, {
    label: "Wavelength Range",
    value: "900 - 2500 nm"
  }, {
    label: "Sampling Mode",
    value: "Reflectance / Transflectance"
  }, {
    label: "Validation",
    value: "IQ / OQ / PQ Ready"
  }]
}, {
  id: "powder-testing",
  label: "Powder & Flow Characterization",
  icon: Layers,
  badge: "Formulation & Processing",
  title: "Powder Rheology & Bulk Density Testing",
  description: "Comprehensive evaluation of powder flowability, caking tendencies, wall friction, and compressibility to prevent hopper blockages and optimize blending.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_station.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_station.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_station.png-wvc-sizes",
    alt: "Laboratory powder testing and flow characterization equipment"
  },
  pageLink: "/PowderTestingPage",
  keyParameters: ["Dynamic flow energy and shear cell testing", "Compressibility, aeration, and permeability metrics", "Moisture sensitivity and consolidation caking profiles", "Applicable to dairy powders, starches, spices, and nutraceuticals"],
  specs: [{
    label: "Sample Size",
    value: "10 ml to 200 ml"
  }, {
    label: "Shear Stress Range",
    value: "0.5 to 50 kPa"
  }, {
    label: "Testing Standards",
    value: "ASTM D6773, USP <1174>"
  }, {
    label: "Data Export",
    value: "CSV, PDF, LIMS integration"
  }]
}, {
  id: "tablet-texture",
  label: "Physical & Tablet Testing",
  icon: Activity,
  badge: "Finished Product QC",
  title: "Physical Property & Texture Analysis",
  description: "Automated and manual testing systems for tablet hardness, disintegration, friability, dissolution, and structural integrity in confectionery and nutraceuticals.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_analyzer.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_analyzer.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_analyzer.png-wvc-sizes",
    alt: "Physical tablet testing and hardness tester device"
  },
  pageLink: "/TabletTestingPage",
  keyParameters: ["Multi-parameter measuring: thickness, diameter, weight, and hardness", "Automated sample positioning with anti-jam transport", "EP, USP, and JP pharmacopeia compliance built-in", "Calibrated load cells with traceable calibration tools"],
  specs: [{
    label: "Hardness Range",
    value: "4 - 800 N"
  }, {
    label: "Throughput",
    value: "Up to 30 samples/run"
  }, {
    label: "Measurement Accuracy",
    value: "± 1 N / ± 0.01 mm"
  }, {
    label: "Interface",
    value: "Touchscreen & PC Software"
  }]
}, {
  id: "raw-material-id",
  label: "Raw Material ID & Safety",
  icon: Microscope,
  badge: "Incoming Goods Verification",
  title: "Handheld & Benchtop Raw Material Verification",
  description: "Instant pass/fail verification of incoming raw materials, excipients, and ingredients right at the receiving dock, eliminating quarantine bottlenecks.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_id_scanner.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_id_scanner.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_id_scanner.png-wvc-sizes",
    alt: "Handheld raw material identification scanner in clean facility"
  },
  pageLink: "/RawMaterialIdentificationPage",
  keyParameters: ["Testing through transparent packaging, bags, and containers", "Extensive pre-built ingredient and compound reference libraries", "Non-destructive testing preventing sample contamination", "Wireless report generation with user role permissions"],
  specs: [{
    label: "Scan Time",
    value: "Application dependent"
  }, {
    label: "Battery Life",
    value: "> 8 Hours continuous"
  }, {
    label: "Laser Safety",
    value: "Class 1 in operation"
  }, {
    label: "Ingress Protection",
    value: "IP65 rated housing"
  }]
}];
const highlights = [{
  icon: ShieldCheck,
  title: "Regulatory Compliance",
  description: "Fully verified instrumentation tailored to industry and laboratory quality standards."
}, {
  icon: Sliders,
  title: "Application Support",
  description: "Method development, chemometric calibration, and qualification executed by analytical specialists."
}, {
  icon: FileCheck2,
  title: "Lifecycle Support",
  description: "Preventative maintenance, routine calibration, and emergency service across Ireland and the UK."
}];
export default function TechnologiesOverview() {
  const [activeTab, setActiveTab] = useState("spectroscopy");
  const prefersReducedMotion = useReducedMotion();
  const animationVariant = prefersReducedMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    }
  };
  return <section id="testingtechnologiesoverview" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container flex flex-col gap-12">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto gap-4">
          <Badge variant="secondary" className="px-3 py-1 font-medium">
            Food & Ingredient Analytical Technologies
          </Badge>

          <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Technologies & Instruments for Rigorous Quality Testing
          </h2>

          <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            Reliable instruments, in-line process analytical technology, and benchtop testing equipment designed for food processors, ingredient manufacturers, and regulated QC laboratories.
          </p>
        </div>

        {/* Multi-Section Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col gap-8">
          <div className="flex justify-center overflow-x-auto pb-2">
            <TabsList className="h-auto p-1.5 bg-muted/80 rounded-xl border border-border flex-wrap justify-center gap-1">
              {technologyCategories.map((tech, i) => {
              const IconComponent = tech.icon;
              return <TabsTrigger key={tech.id} value={tech.id} data-index={i} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-[color,box-shadow]">
                    <IconComponent className="size-4" />
                    <span>{tech.label}</span>
                  </TabsTrigger>;
            })}
            </TabsList>
          </div>

          {technologyCategories.map((tech, i) => <TabsContent key={tech.id} value={tech.id} data-index={i} className="mt-0">
              <motion.div key={tech.id} initial={animationVariant.initial} animate={animationVariant.animate} transition={{
            duration: 0.4,
            ease: "easeOut"
          }}>
                <Card className="border border-border/80 shadow-md overflow-hidden bg-card text-card-foreground">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 md:p-10 items-center">
                    {/* Visual Media Column */}
                    <div className="lg:col-span-5 flex flex-col gap-4">
                      <div className="relative overflow-hidden rounded-xl border border-border bg-muted/30 aspect-[4/3]">
                        <img src={tech.image.src} data-wvc-srcset={tech.image.srcset} data-wvc-sizes={tech.image.sizes} alt={tech.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <div className="absolute top-3 left-3">
                          <Badge variant="default" className="shadow-xs font-semibold">
                            {tech.badge}
                          </Badge>
                        </div>
                      </div>

                      {/* Technical Specs Grid */}
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        {tech.specs.map((spec, sIdx) => <div key={spec.label} data-index={sIdx} className="bg-muted/40 rounded-lg p-2.5 border border-border/50 flex flex-col">
                            <span className="text-xs text-muted-foreground font-medium">
                              {spec.label}
                            </span>
                            <span className="text-xs md:text-sm font-semibold text-foreground mt-0.5">
                              {spec.value}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    {/* Content & Details Column */}
                    <div className="lg:col-span-7 flex flex-col justify-between h-full gap-6">
                      <div className="flex flex-col gap-3">
                        <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                          {tech.title}
                        </h3>
                        <p className="font-default text-base text-muted-foreground leading-relaxed">
                          {tech.description}
                        </p>
                      </div>

                      <Separator className="bg-border" />

                      {/* Key Capabilities */}
                      <div className="flex flex-col gap-3">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                          Key Capabilities & Testing Highlights
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          {tech.keyParameters.map((param, pIdx) => <div key={pIdx} data-index={pIdx} className="flex items-start gap-2 text-sm text-foreground/90">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span className="font-default leading-snug">{param}</span>
                            </div>)}
                        </div>
                      </div>

                      <Separator className="bg-border" />

                      {/* CTA Actions */}
                      <div className="flex flex-wrap items-center gap-4 pt-2">
                        <Button asChild size="lg" className="font-medium">
                          <Link to={tech.pageLink}>
                            <span>Explore Application Details</span>
                            <ArrowRight className="size-4" />
                          </Link>
                        </Button>
                        <Button asChild variant="outline" size="lg" className="font-medium">
                          <Link to="/ContactPage">Request Technical Consultation</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </TabsContent>)}
        </Tabs>

        {/* Support & Feature Highlights Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
          {highlights.map((item, i) => {
          const IconEl = item.icon;
          return <div key={item.title} data-index={i} className="bg-card border border-border rounded-xl p-6 shadow-xs flex flex-col gap-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <IconEl className="size-5" />
                </div>
                <h4 className="font-default text-lg font-bold text-foreground">
                  {item.title}
                </h4>
                <p className="font-default text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}